import Cookies from 'js-cookie';
import React, { FC, useState, ChangeEvent, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';

import { Match, Matches, PlayerResult, UpdateStatus } from '../../../../../database.schema';
import { Course, Player } from '../../../../../database.schema';
import { MatchEntryPairs } from './match-entry-pairs';
import { MatchEntrySingles } from './match-entry-singles';
import { UpdateButton } from './update-button';
import { FlexRowStart } from '../../../common-styles/common-styles';
import { AWAITING_SCORES } from '../../../../app/constants';
import { MatchEntryInput, MatchEntryLabel } from '../../match-entry-styles';
import { MatchEntryDataData, useFetchMatchEntryData } from '../../../../api/hooks/use-fetch-match-entry-data';
import { SelectedIndexContext } from '../../../../App';

export const TeamWrapper = styled.div<{ bgColor?: string | null }>`
  border: 1px solid black;
  border-radius: 0.25rem;
  width: 8.25rem;
  background: ${(props) => props.bgColor || '#eae6e6'};
  color: white;
  padding: 5px;
  min-height: 9rem;
  margin: 0.25rem 0;
  text-align: center;
`;
const Teams = styled.div`
  display: flex;
  justify-content: space-evenly;
  width: 22rem;
`;
const DragablePlayer = styled.div`
  margin: 0.25rem;
`;
const CourseDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 0.5rem 0;
  select {
    height: 1.5rem;
    margin-left: 0.25rem;
  }
  label {
    margin-right: 0.5rem;
  }
`;
const FlexEven = styled.div`
  display: flex;
  justify-content: space-evenly;
  margin-top: 1rem;
`;

const Button = styled.button`
  border-radius: 5px;
  width: 4rem;
  height: 1.5rem;
  background: lightblue;
  border: 1px solid #5c9db2;
  font-weight: 500;
  color: white;
  margin: 0.5rem 0 1rem;
  &:disabled {
    background-color: lightgray;
  }
`;

const resultByHole = ['-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-', '-'];

const createResultsHolder = (playersPerTeam: number, resultsType: string, courseId: number): Matches => {
  const recordedMatch: Match = { status: AWAITING_SCORES, date: '', team1: { players: [], points: 0 }, team2: { players: [], points: 0 }, resultByHole };
  const resultHolder: Matches = { courseId, format: resultsType.toLocaleLowerCase() as 'Pairs' | 'Singles', matches: [], startingHole: 1 };
  let index = 0;
  const singlePlayer: PlayerResult[] = [
    {
      playerId: -1,
      points: 0,
      won: 0,
      lost: 0,
      halved: 0,
      captainPoints: 0,
      round: { playerId: -1, courseHandicap: -1, individualHandicap: -1, shotsReceived: -1, scoreCard: [], points: 0 },
      nearestThePin: 0,
      nearestThePinIn2: 0,
      longestDrive: 0,
    },
  ];
  if (resultsType === 'Pairs') {
    const matchHasSingles = playersPerTeam % 2 !== 0;
    const numberOfMatches = Math.round(playersPerTeam / 2);
    const pairsPlayers = [...singlePlayer, ...singlePlayer];
    for (index; index < numberOfMatches; index++) {
      resultHolder.matches.push({ ...recordedMatch });
      const players = matchHasSingles && index === 0 ? singlePlayer : pairsPlayers;
      resultHolder.matches[index].team1.players = [...players];
      resultHolder.matches[index].team2.players = [...players];
    }
  } else {
    for (index; index < playersPerTeam; index++) {
      resultHolder.matches.push({ ...recordedMatch });
      resultHolder.matches[index].team1.players = [...singlePlayer];
      resultHolder.matches[index].team2.players = [...singlePlayer];
    }
  }
  return resultHolder;
};

type Props = {
  setShowReset: (value: boolean) => void;
  recordedMatches: Matches;
};

export const MatchEntry: FC<Props> = ({ setShowReset, recordedMatches }): JSX.Element | null => {
  const navigate = useNavigate();
  const { selectedTournamentIndex } = useContext(SelectedIndexContext);
  const { isLoading, error, data } = useFetchMatchEntryData(selectedTournamentIndex);
  const { team1, team2, nextResultIndex, playersPerTeam, courses } = data as MatchEntryDataData;

  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>({ success: false, error: false, message: '', stack: '' });
  const [resultsValid, setResultsValid] = useState(false);
  const [resultsFormat, setResultsFormat] = useState('Pairs');
  const [recordedMatchesStore, setRecordedMatchesStore] = useState(createResultsHolder(playersPerTeam, resultsFormat, recordedMatches.courseId));

  const [dndTeam1Players, setDndTeam1Players] = useState<Array<Player>>(team1);
  const [dndTeam2Players, setDndTeam2Players] = useState<Array<Player>>(team2);

  const onFormatChange = (e: ChangeEvent<HTMLInputElement>) => {
    setRecordedMatchesStore(createResultsHolder(playersPerTeam, e.target.value, recordedMatchesStore.courseId));
    setDndTeam1Players(team1);
    setDndTeam2Players(team2);
    setResultsFormat(e.target.value);
  };

  const isNotValid = (): void => {
    let valid = true;
    const form = document.querySelector('form')!;
    if (form) {
      const formData = new FormData(form);
      formData.forEach((value, key) => {
        if (value === '') {
          if (key.startsWith('team-result-margin-')) {
            const index = key.substring(key.length - 1);
            if (formData.get(`team-result-${index}`) === 'beat' || formData.get(`team-result-${index}`) === 'lost to') {
              valid = false;
            }
          } else {
            valid = false;
          }
          return;
        }
      });
    }
    setResultsValid(valid);
  };

  const onDragEnd = (result: DropResult): void => {
    if (!result.destination) {
      return;
    }
    if (result.destination.droppableId === result.source.droppableId) {
      return;
    }

    if (
      (result.destination.droppableId.startsWith('team1') && result.source.droppableId.startsWith('team2')) ||
      (result.destination.droppableId.startsWith('team2') && result.source.droppableId.startsWith('team1'))
    ) {
      return;
    }

    const clone = JSON.parse(JSON.stringify(recordedMatchesStore));
    if (result.destination.droppableId.startsWith('team1Single') && result.source.droppableId.startsWith('team1')) {
      const index = Number(result.destination.droppableId.split('-')[1]);
      const dragPlayer = team1.find((player) => player.id === Number(result.draggableId));
      if (dragPlayer) {
        clone.matches[index].team1.players[0].playerId = Number(result.draggableId);
        setRecordedMatchesStore(clone);
      }
    }

    if (result.destination.droppableId.startsWith('team1PairPlayer1') && result.source.droppableId.startsWith('team1')) {
      const index = Number(result.destination.droppableId.split('-')[1]);
      const dragPlayer = team1.find((player) => player.id === Number(result.draggableId));
      if (dragPlayer) {
        clone.matches[index].team1.players[0].playerId = dragPlayer.id;
        setRecordedMatchesStore(clone);
      }
    }

    if (result.destination.droppableId.startsWith('team1PairPlayer2') && result.source.droppableId.startsWith('team1')) {
      const index = Number(result.destination.droppableId.split('-')[1]);
      const dragPlayer = team1.find((player) => player.id === Number(result.draggableId));
      if (dragPlayer) {
        clone.matches[index].team1.players[1].playerId = dragPlayer.id;
        setRecordedMatchesStore(clone);
      }
    }

    if (result.destination.droppableId.startsWith('team2Single') && result.source.droppableId.startsWith('team2')) {
      const index = Number(result.destination.droppableId.split('-')[1]);
      const dragPlayer = team2.find((player) => player.id === Number(result.draggableId));
      if (dragPlayer) {
        clone.matches[index].team2.players[0].playerId = dragPlayer.id;
        setRecordedMatchesStore(clone);
      }
    }

    if (result.destination.droppableId.startsWith('team2PairPlayer1') && result.source.droppableId.startsWith('team2')) {
      const index = Number(result.destination.droppableId.split('-')[1]);
      const dragPlayer = team2.find((player) => player.id === Number(result.draggableId));
      if (dragPlayer) {
        clone.matches[index].team2.players[0].playerId = dragPlayer.id;
        setRecordedMatchesStore(clone);
      }
    }

    if (result.destination.droppableId.startsWith('team2PairPlayer2') && result.source.droppableId.startsWith('team2')) {
      const index = Number(result.destination.droppableId.split('-')[1]);
      const dragPlayer = team2.find((player) => player.id === Number(result.draggableId));
      if (dragPlayer) {
        clone.matches[index].team2.players[1].playerId = dragPlayer.id;
        setRecordedMatchesStore(clone);
      }
    }

    if (result.source.droppableId.startsWith('team1')) {
      const newState = [...dndTeam1Players];
      newState.splice(result.source.index, 1);
      setDndTeam1Players(newState);
    }
    if (result.source.droppableId.startsWith('team2')) {
      const newState = [...dndTeam2Players];
      newState.splice(result.source.index, 1);
      setDndTeam2Players(newState);
    }
  };

  const authToken = Cookies.get('token');
  if (!authToken) {
    navigate('/login');
  }

  if (isLoading) {
    return <div>loading...</div>;
  }

  if (error) {
    return <div>...error</div>;
  }
  return (
    <DragDropContext onDragEnd={onDragEnd}>
      {updateStatus.error && (
        <div>
          Update failed!!!! ${updateStatus.message} - ${authToken}
        </div>
      )}
      <form id="form" name="results" onSubmit={(event) => event.preventDefault()} onChange={() => isNotValid()} autoComplete="off">
        <h2>Create Matches: Day {nextResultIndex + 1}</h2>
        <CourseDisplay>
          <div>
            <strong>Course:</strong>
            <select
              name="course"
              data-testid="course"
              onChange={(e) => {
                setRecordedMatchesStore({ ...recordedMatchesStore, courseId: Number(e.target.value) });
              }}
            >
              <option value="">Course...</option>
              {courses.map((course: Course) => (
                <option key={course.name} value={`${course.id}`}>
                  {course.name}
                </option>
              ))}
            </select>
          </div>
        </CourseDisplay>
        <div>
          <FlexRowStart>
            <strong>Starting Hole:</strong>
            <MatchEntryInput
              type="radio"
              name="startingHole"
              value="first"
              id="1"
              data-testid="first"
              checked={recordedMatchesStore.startingHole === 1}
              onChange={() => {
                setRecordedMatchesStore({ ...recordedMatchesStore, startingHole: 1 });
              }}
            />
            <MatchEntryLabel htmlFor="pairs">First</MatchEntryLabel>
            <MatchEntryInput
              type="radio"
              name="startingHole"
              value="tenth"
              id="10"
              data-testid="tenth"
              checked={recordedMatchesStore.startingHole === 10}
              onChange={() => {
                setRecordedMatchesStore({ ...recordedMatchesStore, startingHole: 10 });
              }}
            />
            <MatchEntryLabel htmlFor="singles">Tenth</MatchEntryLabel>
          </FlexRowStart>
        </div>
        <FlexRowStart>
          <strong>Format:</strong>
          <MatchEntryInput type="radio" name="resultType" value="Pairs" id="pairs" data-testid="pairs" checked={resultsFormat === 'Pairs'} onChange={onFormatChange} />
          <MatchEntryLabel htmlFor="pairs">Pairs</MatchEntryLabel>
          <MatchEntryInput type="radio" name="resultType" value="Singles" id="singles" data-testid="singles" checked={resultsFormat === 'Singles'} onChange={onFormatChange} />
          <MatchEntryLabel htmlFor="singles">Singles</MatchEntryLabel>
        </FlexRowStart>
        <Teams>
          <TeamWrapper bgColor="#f89595">
            <Droppable droppableId="team1">
              {(provided, snapshot): JSX.Element => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {dndTeam1Players.map((player: Player, index) => (
                    <Draggable key={`team1-${player.id}`} draggableId={`${player.id}`} index={index}>
                      {(provided, snapshot): JSX.Element => (
                        <DragablePlayer ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                          <div style={{ color: 'white', height: '2.25rem', width: '7rem', background: 'red', borderRadius: '0.25rem', padding: '0.5rem' }}>{player.nickName}</div>
                        </DragablePlayer>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </TeamWrapper>
          <TeamWrapper bgColor="#bebaf4">
            <Droppable droppableId="team2">
              {(provided, snapshot): JSX.Element => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {dndTeam2Players.map((player: Player, index) => (
                    <Draggable key={`team2-${player.id}`} draggableId={`${player.id}`} index={index}>
                      {(provided, snapshot): JSX.Element => (
                        <DragablePlayer ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                          <div style={{ color: 'white', height: '2.25rem', width: '7rem', background: 'blue', borderRadius: '0.25rem', padding: '0.5rem' }}>{player.nickName}</div>
                        </DragablePlayer>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </TeamWrapper>
        </Teams>
        <FlexEven>
          {resultsFormat === 'Pairs' ? (
            <MatchEntryPairs resultsHolder={recordedMatchesStore} playersPerTeam={team1.length} />
          ) : (
            <MatchEntrySingles resultsHolder={recordedMatchesStore} />
          )}
        </FlexEven>
        <FlexEven>
          <UpdateButton
            selectedTournamentIndex={selectedTournamentIndex}
            resultIndex={nextResultIndex}
            matches={{ ...recordedMatchesStore }}
            setShowUpdate={setShowReset}
            setUpdateStatus={setUpdateStatus}
            disabled={!resultsValid}
          />
          <Button type="button" onClick={() => (setShowReset ? setShowReset(false) : navigate('/'))}>
            Cancel
          </Button>
        </FlexEven>
      </form>
    </DragDropContext>
  );
};
